/**
 * The upgrade dialog's decisions and wording, kept out of the component file so
 * it exports only React components (Fast Refresh).
 */
import { FEATURE_LABELS, type PlanTier } from '@/context/plan-constraint-utils'
import {
  cheapestOnSaleFrom,
  DEFAULT_ON_SALE_TIERS,
  nextTierAbove,
  tierThatUnlocks,
} from './upgrade-plan-gains'
import type { UpgradePromptRequest } from './upgrade-prompt-store'

/**
 * What to say when the plan cannot be improved on. Named and exported so the
 * wording is testable without rendering, and so it cannot drift from the
 * dialog's own copy.
 */
export function exhaustedMessage(request: UpgradePromptRequest): string {
  const label = request.context?.label ?? FEATURE_LABELS[request.feature]
  const limit = request.context?.limit
  return limit != null
    ? `You have used all ${limit} of your ${label} allowance for this billing cycle.`
    : `You have used your ${label} allowance for this billing cycle.`
}

/**
 * The toast for a refusal no purchase can answer: a spent allowance with
 * nothing on sale above it, or any refusal in beta, which has no billing page.
 */
export function unavailableMessage(request: UpgradePromptRequest): string {
  if (request.reason === 'limit') return exhaustedMessage(request)
  const label = request.context?.label ?? FEATURE_LABELS[request.feature]
  return `${label} is not included in your plan.`
}

/**
 * Which plan to sell. An explicit tier wins; otherwise a locked feature is
 * answered by the cheapest tier granting it, and a spent allowance by the next
 * tier up, because the plan already has the feature. Either way the pitch is
 * the cheapest tier at or above that one which is actually `onSale`. Null
 * means there is nothing to sell.
 */
export function resolveTargetTier(
  request: UpgradePromptRequest,
  planTier: PlanTier,
  onSale: readonly PlanTier[] = DEFAULT_ON_SALE_TIERS,
): PlanTier | null {
  const floor = request.requiredTier
    ? request.requiredTier
    : request.reason === 'limit'
      ? nextTierAbove(planTier)
      : (tierThatUnlocks(request.feature, planTier) ?? nextTierAbove(planTier))
  return floor ? cheapestOnSaleFrom(floor, onSale) : null
}
